import React from 'react';
import { Box, ImageListItem, ImageListItemBar } from '@mui/material';
import {
  focusedThumbnailSx,
  getListItemSx,
  listImageAnimatedActionIconSx,
  listImageBarActionsBottomSx,
  listImageBarFullTitleSx,
  listImageBarSx,
  selectedThumbnailOutlineSx,
  selectedThumbnailOverlaySx,
} from '../../styles';

/** What every tile has, whatever its file: its place in the list, its title bar and its state. */
export interface ListItemFrameProps {
  thumbnailSize: number;
  listItemRef: React.RefObject<HTMLLIElement>;
  onClick: (e: React.SyntheticEvent) => void;
  showActions: boolean;
  setShowActions: React.Dispatch<React.SetStateAction<boolean>>;
  actionIcon: React.ReactNode;
  /** The title on the bar. */
  description: string;
  /** A line under the title, e.g. what a linked documentation shows. */
  caption?: string;
  showFullTitle: boolean;
  selected?: boolean;
  focused?: boolean;
}

/**
 * The action icon on the bar: with the full title it is always there and only fades
 * in on hover (the bar keeps its height); otherwise it shows on hover only.
 */
function resolveActionIcon(
  showFullTitle: boolean,
  showActions: boolean,
  actionIcon: React.ReactNode
): React.ReactNode {
  if (showFullTitle) {
    return <Box sx={listImageAnimatedActionIconSx(showActions)}>{actionIcon}</Box>;
  }
  return showActions ? actionIcon : undefined;
}

/**
 * A tile of the file list: the selection and focus around it, the actions shown on
 * hover, and the bar with the title and the caption. The thumbnail is the child, so
 * each kind of file only says how it is previewed.
 */
export function ListItemFrame({
  thumbnailSize,
  listItemRef,
  onClick,
  showActions,
  setShowActions,
  actionIcon,
  description,
  caption,
  showFullTitle,
  selected = false,
  focused = false,
  children,
}: React.PropsWithChildren<ListItemFrameProps>): React.ReactElement {
  return (
    <ImageListItem
      ref={listItemRef}
      sx={[getListItemSx(thumbnailSize, true), selected && selectedThumbnailOutlineSx, focused && focusedThumbnailSx]}
      onMouseEnter={() => setShowActions(true)}
      onMouseLeave={() => setShowActions(false)}
    >
      {selected && <Box sx={selectedThumbnailOverlaySx} />}
      {children}
      <ImageListItemBar
        title={description}
        subtitle={caption}
        actionIcon={resolveActionIcon(showFullTitle, showActions, actionIcon)}
        onClick={onClick}
        sx={showFullTitle ? [listImageBarSx, listImageBarFullTitleSx, listImageBarActionsBottomSx] : listImageBarSx}
      />
    </ImageListItem>
  );
}
